<template>
  <!-- Premium Transaction Details Modal -->
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-list-ul me-2"></i>
            Premium Payment Transactions - {{ collector?.collector_name }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- Summary Card -->
          <div class="card bg-light mb-4">
            <div class="card-body">
              <div class="row text-center">
                <div class="col-md-3">
                  <h4 class="text-primary">{{ summary.total_transactions }}</h4>
                  <p class="mb-0 text-muted">Total Transactions</p>
                </div>
                <div class="col-md-3">
                  <h4 class="text-success">{{ formatCurrency(summary.total_amount) }}</h4>
                  <p class="mb-0 text-muted">Total Amount</p>
                </div>
                <div class="col-md-3">
                  <h4 class="text-warning">{{ selectedTransactions.length }}</h4>
                  <p class="mb-0 text-muted">Selected</p>
                </div>
                <div class="col-md-3">
                  <h4 class="text-info">{{ formatCurrency(selectedAmount) }}</h4>
                  <p class="mb-0 text-muted">Selected Amount</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success" role="alert">
            <i class="bx bx-check-circle me-2"></i>
            {{ message }}
          </div>
          <div v-if="alert.failed" class="alert alert-danger" role="alert">
            <i class="bx bx-error-circle me-2"></i>
            {{ message }}
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="btn-group" role="group">
              <button
                  @click="selectAllPendingTransactions"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="pendingTransactions.length === 0"
              >
                <i class="bx bx-check-square me-1"></i>
                Select All Pending ({{ pendingTransactions.length }})
              </button>
              <button
                  @click="clearSelection"
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="selectedTransactions.length === 0"
              >
                <i class="bx bx-x me-1"></i>
                Clear Selection
              </button>
              <button
                  @click="toggleHandedOverDisplay"
                  class="btn btn-outline-info btn-sm"
              >
                <i class="bx bx-show me-1"></i>
                {{ showHandedOver ? 'Hide' : 'Show' }} Handed Over
              </button>
            </div>

            <button
                @click="showHandoverForm = true"
                class="btn btn-success"
                :disabled="selectedTransactions.length === 0 || processing"
            >
              <i class="bx bx-check-circle me-1"></i>
              Process Handover ({{ selectedTransactions.length }})
            </button>
          </div>

          <!-- Transactions Table -->
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead class="table-light">
              <tr>
                <th width="50">
                  <input
                      type="checkbox"
                      @change="toggleAllPendingTransactions"
                      :checked="isAllPendingSelected"
                      :indeterminate.prop="isPartiallySelected"
                      class="form-check-input"
                  >
                </th>
                <th>Payment Details</th>
                <th>Member Info</th>
                <th class="text-end">Amount</th>
                <th class="text-center">Payment Date</th>
                <th class="text-center">Location</th>
                <th class="text-center">Status</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-2 mb-0">Loading transactions...</p>
                </td>
              </tr>
              <tr v-else-if="displayedTransactions.length === 0">
                <td colspan="7" class="text-center py-4 text-muted">
                  <i class="bx bx-receipt bx-lg d-block mb-2"></i>
                  No transactions found for this collector
                </td>
              </tr>
              <tr v-else v-for="transaction in displayedTransactions" :key="transaction.payment_id">
                <td>
                  <input
                      type="checkbox"
                      :value="transaction.payment_id"
                      v-model="selectedTransactions"
                      class="form-check-input"
                      :disabled="transaction.handed_over == 1"
                  >
                </td>
                <td>
                  <div>
                    <strong class="d-block text-primary">Payment ID: {{ transaction.payment_id }}</strong>
                    <small class="text-muted d-block">Ref: {{ transaction.payment_reference || 'N/A' }}</small>
                    <small class="text-muted d-block">Receipt: {{ transaction.receipt_number || 'N/A' }}</small>
                    <small class="text-success d-block">Year: {{ transaction.payment_year }}</small>
                  </div>
                </td>
                <td>
                  <div>
                    <strong class="d-block">{{ transaction.first_name }} {{ transaction.last_name }}</strong>
                    <small class="text-muted d-block">CBHI: {{ transaction.cbhi_number || 'N/A' }}</small>
                    <small class="text-muted d-block">Phone: {{ transaction.phone_number || 'N/A' }}</small>
                  </div>
                </td>
                <td class="text-end">
                  <strong class="text-success fs-6">{{ formatCurrency(transaction.payment_amount) }}</strong>
                </td>
                <td class="text-center">
                  <small>{{ formatDate(transaction.payment_date) }}</small>
                </td>
                <td class="text-center">
                  <small class="text-muted">
                    <div v-if="transaction.woreda_name">{{ transaction.woreda_name }}</div>
                    <div v-if="transaction.zone_name">{{ transaction.zone_name }}</div>
                  </small>
                </td>
                <td class="text-center">
                    <span v-if="transaction.handed_over == 1" class="badge bg-success">
                      <i class="bx bx-check me-1"></i>Handed Over
                    </span>
                  <span v-else class="badge bg-warning text-dark">
                      <i class="bx bx-time me-1"></i>Pending
                    </span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Handover Form -->
          <div v-if="showHandoverForm" class="card border-success mt-4">
            <div class="card-header bg-success text-white">
              <h6 class="mb-0">
                <i class="bx bx-check-circle me-2"></i>
                Process Premium Payment Handover
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Expected Amount</label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          :value="selectedAmount.toFixed(2)"
                          type="text"
                          class="form-control"
                          readonly
                      >
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Actual Amount Received *</label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          v-model.number="handoverForm.actualAmount"
                          type="number"
                          step="0.01"
                          class="form-control"
                          :class="{ 'is-invalid': handoverForm.actualAmount !== selectedAmount }"
                          placeholder="Enter actual cash amount"
                          required
                      >
                    </div>
                    <small v-if="varianceAmount !== 0" :class="varianceAmount > 0 ? 'text-success' : 'text-danger'">
                      Variance: {{ formatCurrency(varianceAmount) }} ({{ varianceAmount > 0 ? 'Overage' : 'Shortage' }})
                    </small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Number of Transactions</label>
                    <input
                        :value="selectedTransactions.length"
                        type="number"
                        class="form-control"
                        readonly
                    >
                  </div>
                </div>
                <div class="col-12">
                  <div class="mb-3">
                    <label class="form-label">Notes (Optional)</label>
                    <textarea
                        v-model="handoverForm.notes"
                        class="form-control"
                        rows="3"
                        placeholder="Any additional notes about this handover (variance explanations, etc.)..."
                    ></textarea>
                  </div>
                </div>
              </div>

              <div class="alert alert-info">
                <i class="bx bx-info-circle me-2"></i>
                <strong>Please verify the physical cash count before proceeding.</strong>
                The handover will be recorded under your name as the receiving manager.
              </div>

              <div v-if="Math.abs(varianceAmount) > 0.01" class="alert alert-warning">
                <i class="bx bx-error-alt me-2"></i>
                <strong>Variance Detected:</strong> There is a difference between expected and actual amounts.
                Please provide explanation in notes and verify the count.
              </div>

              <div class="d-flex justify-content-end gap-2">
                <button
                    @click="cancelHandover"
                    class="btn btn-secondary"
                    :disabled="processing"
                >
                  Cancel
                </button>
                <button
                    @click="confirmHandover"
                    class="btn btn-success"
                    :disabled="processing || !handoverForm.actualAmount"
                >
                  <span v-if="processing">
                    <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                    Processing...
                  </span>
                  <span v-else>
                    <i class="bx bx-check me-1"></i>
                    Confirm Handover
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop -->
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "PremiumTransactionDetailsModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    collector: {
      type: Object,
      default: () => null
    },
    dateFrom: {
      type: String,
      required: true
    },
    dateTo: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      processing: false,
      transactions: [],
      selectedTransactions: [],
      showHandoverForm: false,
      showHandedOver: false,

      summary: {
        collector_name: '',
        total_transactions: 0,
        total_amount: 0,
        pending_amount: 0,
        handed_over_amount: 0
      },

      handoverForm: {
        actualAmount: 0,
        notes: ''
      },

      // Alert states
      alert: {
        success: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    selectedAmount() {
      return this.transactions
          .filter(t => this.selectedTransactions.includes(t.payment_id))
          .reduce((sum, t) => sum + parseFloat(t.payment_amount), 0);
    },

    varianceAmount() {
      return this.handoverForm.actualAmount - this.selectedAmount;
    },

    pendingTransactions() {
      return this.transactions.filter(t => t.handed_over != 1);
    },

    displayedTransactions() {
      return this.showHandedOver ? this.transactions : this.pendingTransactions;
    },

    isAllPendingSelected() {
      return this.pendingTransactions.length > 0 &&
          this.pendingTransactions.every(t => this.selectedTransactions.includes(t.payment_id));
    },

    isPartiallySelected() {
      const selectedPending = this.pendingTransactions.filter(t => this.selectedTransactions.includes(t.payment_id));
      return selectedPending.length > 0 && selectedPending.length < this.pendingTransactions.length;
    }
  },

  watch: {
    show(newVal) {
      if (newVal && this.collector) {
        this.loadTransactions();
      } else {
        this.resetModal();
      }
    },

    selectedAmount(newVal) {
      // Auto-set actual amount to expected amount whenever selection changes
      this.handoverForm.actualAmount = newVal;
    }
  },

  methods: {
    clearAlerts() {
      this.alert.success = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      setTimeout(() => {
        this.clearAlerts();
      }, 5000);
    },

    async loadTransactions() {
      this.loading = true;

      const data = {
        "function": "getPremiumCollectorTransactions",
        "collectorName": this.collector.collector_name,
        "dateFrom": this.dateFrom,
        "dateTo": this.dateTo,
        "showHandedOver": true // Show all transactions
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.transactions = res.data.data.transactions || [];
          this.summary = res.data.data.summary || {};
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load transactions');
        }
      } catch (e) {
        this.showAlert('failed', 'Failed to load transactions');
        console.error("Failed to load transactions:", e);
      } finally {
        this.loading = false;
      }
    },

    selectAllPendingTransactions() {
      this.selectedTransactions = this.pendingTransactions.map(t => t.payment_id);
    },

    clearSelection() {
      this.selectedTransactions = [];
    },

    toggleAllPendingTransactions() {
      if (this.isAllPendingSelected) {
        this.clearSelection();
      } else {
        this.selectAllPendingTransactions();
      }
    },

    toggleHandedOverDisplay() {
      this.showHandedOver = !this.showHandedOver;
    },

    async confirmHandover() {
      if (this.selectedTransactions.length === 0) {
        this.showAlert('failed', 'Please select at least one transaction');
        return;
      }

      if (!this.handoverForm.actualAmount) {
        this.showAlert('failed', 'Please enter the actual amount received');
        return;
      }

      this.processing = true;

      const data = {
        "function": "processPremiumHandover",
        "collectorName": this.collector.collector_name,
        "paymentIds": this.selectedTransactions,
        "handoverAmount": this.selectedAmount,
        "actualAmount": this.handoverForm.actualAmount,
        "notes": this.handoverForm.notes
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', 'Handover processed successfully');
          this.$emit('handover-processed', res.data.data);

          // Reload transactions to show updated status
          setTimeout(() => {
            this.loadTransactions();
            this.cancelHandover();
          }, 1500);
        } else {
          this.showAlert('failed', res.data.message || 'Failed to process handover');
        }
      } catch (e) {
        this.showAlert('failed', 'Failed to process handover');
        console.error("Failed to process handover:", e);
      } finally {
        this.processing = false;
      }
    },

    cancelHandover() {
      this.showHandoverForm = false;
      this.handoverForm = {
        actualAmount: 0,
        notes: ''
      };
    },

    resetModal() {
      this.transactions = [];
      this.selectedTransactions = [];
      this.showHandoverForm = false;
      this.showHandedOver = false;
      this.cancelHandover();
      this.clearAlerts();
      this.summary = {
        collector_name: '',
        total_transactions: 0,
        total_amount: 0,
        pending_amount: 0,
        handed_over_amount: 0
      };
    },

    closeModal() {
      this.$emit('close');
    },

    formatCurrency(amount) {
      if (!amount || amount === 0) return 'ETB 0.00';
      return 'ETB ' + parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1054;
}

.form-check-input:indeterminate {
  background-color: #0d6efd;
  border-color: #0d6efd;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/%3e%3c/svg%3e");
}

.table-sm th,
.table-sm td {
  padding: 0.5rem;
  font-size: 0.875rem;
  vertical-align: middle;
}

.btn-sm {
  font-size: 0.8rem;
}

.card-header {
  padding: 0.75rem 1rem;
}

.text-success {
  font-weight: 600;
}

.fs-6 {
  font-size: 0.875rem !important;
}

.badge {
  font-size: 0.7rem;
}

.is-invalid {
  border-color: #dc3545;
}

.alert {
  border: none;
  border-radius: 0.375rem;
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.75rem;
  }

  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}
</style>